import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Modal,
    TextField,
    MenuItem,
    CircularProgress,
    Snackbar,
    Alert,
    IconButton,
    Tooltip,
    TableSortLabel,
    FormControlLabel,
    Switch,
    Autocomplete,
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import axios from 'axios';
import { API_PATH } from '../../../App';
import { getAxiosConfig } from '../../../helpers/AuthHelper';

interface CloudNicehashConfigData {
    id: string;
    name: string;
}

interface Reseller {
    id: number;
    resellerName: string;
}

type CloudOrderProvider = 'NICEHASH' | 'BRAIINS';

interface CloudOrderData {
    id: number | null;
    provider: CloudOrderProvider;
    userPrefix: string;
    password?: string;
    url: string;
    startPort: number;
    shouldIncPort: boolean;
    requiredHash: number;
    totalWorkers: number;
    algorithm: string;
    orderType: string;
    autoDisconnect: boolean;
    resellerId: number;
    cloudNicehashConfigId: string;
    active: boolean;
    hashRateUnit: string;
    maxBidPrice?: number | string | null;
    fallbackMarginPercent: number | string;
    createTime?: string;
    updateTime?: string;
}

const emptyForm: CloudOrderData = {
    id: null,
    provider: 'NICEHASH',
    userPrefix: '',
    password: 'x',
    url: '',
    startPort: 0,
    shouldIncPort: true,
    requiredHash: 0,
    totalWorkers: 0,
    algorithm: 'SHA256ASICBOOST',
    orderType: 'STANDARD',
    autoDisconnect: false,
    resellerId: 0,
    cloudNicehashConfigId: '',
    active: true,
    hashRateUnit: 'EH',
    maxBidPrice: 0,
    fallbackMarginPercent: 0,
};

function CloudOrders() {
    const [orders, setOrders] = useState<CloudOrderData[]>([]);
    const [configs, setConfigs] = useState<CloudNicehashConfigData[]>([]);
    const [formOpen, setFormOpen] = useState(false);
    const [formData, setFormData] = useState<CloudOrderData>(emptyForm);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');
    const [orderBy, setOrderBy] = useState<keyof CloudOrderData>('updateTime');
    const [showActiveOnly, setShowActiveOnly] = useState(true);
    const [resellers, setResellers] = useState<Reseller[]>([]);

    useEffect(() => {
        fetchConfigs();
        fetchOrders();
        fetchResellers();
    }, []);

    const fetchResellers = () => {
        axios
            .get(API_PATH + '/customer_ledger/allResellers', getAxiosConfig())
            .then((res) => setResellers(res.data))
            .catch(() => console.error('Failed to fetch resellers'));
    };

    const fetchConfigs = () => {
        axios
            .get(API_PATH + '/nicehash/cloud/order/config', getAxiosConfig())
            .then((res) => setConfigs(res.data))
            .catch(() => console.error('Failed to fetch configs'));
    };

    const fetchOrders = () => {
        setFetching(true);
        axios
            .get(API_PATH + '/nicehash/cloud/orders', getAxiosConfig())
            .then((res) => setOrders(res.data.map((item: Partial<CloudOrderData>) => ({
                ...item,
                provider: item.provider || 'NICEHASH',
                fallbackMarginPercent: item.fallbackMarginPercent ?? 0,
            })) as CloudOrderData[]))
            .catch(() => setSnackbar({ open: true, message: 'Failed to fetch cloud orders.', severity: 'error' }))
            .finally(() => setFetching(false));
    };

    const handleOpenAdd = () => {
        setFormData(emptyForm);
        setFormOpen(true);
    };

    const handleOpenEdit = (row: CloudOrderData) => {
        setFormData({ ...row, provider: row.provider || 'NICEHASH' });
        setFormOpen(true);
    };

    const handleClose = () => setFormOpen(false);

    const handleChange = (field: keyof CloudOrderData) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.type === 'checkbox' ? (e.target as any).checked : e.target.value;
        setFormData({ ...formData, [field]: val });
    };

    const handleSave = () => {
        if (!formData.hashRateUnit) {
            setSnackbar({ open: true, message: 'Please specify a Hash Rate Unit before saving.', severity: 'warning' });
            return;
        }
        setLoading(true);
        axios
            .post(API_PATH + '/nicehash/cloud/orders', formData, getAxiosConfig())
            .then(() => {
                setSnackbar({ open: true, message: 'Cloud order saved successfully!', severity: 'success' });
                fetchOrders();
                handleClose();
            })
            .catch(() => setSnackbar({ open: true, message: 'Failed to save cloud order.', severity: 'error' }))
            .finally(() => setLoading(false));
    };

    const handleToggleActive = (row: CloudOrderData) => {
        if (!window.confirm(`Are you sure you want to ${row.active ? 'disable' : 'enable'} this cloud order?`)) return;
        setLoading(true);
        const updatedRow = { ...row, active: !row.active };
        axios
            .post(API_PATH + '/nicehash/cloud/orders', updatedRow, getAxiosConfig())
            .then(() => {
                setSnackbar({ open: true, message: `Cloud order ${updatedRow.active ? 'enabled' : 'disabled'} successfully!`, severity: 'success' });
                fetchOrders();
            })
            .catch(() => setSnackbar({ open: true, message: 'Failed to update cloud order.', severity: 'error' }))
            .finally(() => setLoading(false));
    };

    const handleRequestSort = (property: keyof CloudOrderData) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const sortedOrders = [...orders].sort((a, b) => {
        let valA = a[orderBy] || '';
        let valB = b[orderBy] || '';

        // Handle sorting for timestamps
        if (orderBy === 'createTime' || orderBy === 'updateTime') {
            const timeA = a[orderBy] ? new Date(a[orderBy] as string).getTime() : 0;
            const timeB = b[orderBy] ? new Date(b[orderBy] as string).getTime() : 0;
            return order === 'desc' ? timeA - timeB : timeB - timeA;
        }

        if (valA < valB) return order === 'asc' ? -1 : 1;
        if (valA > valB) return order === 'asc' ? 1 : -1;
        return 0;
    });

    return (
        <Box p={3}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Typography variant="h5" fontWeight="bold">
                    Cloud Orders
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                    <Button variant="contained" startIcon={<Add />} onClick={handleOpenAdd}>
                        Add New Order
                    </Button>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showActiveOnly}
                                onChange={(e) => setShowActiveOnly(e.target.checked)}
                                color="primary"
                            />
                        }
                        label="Show Active Only"
                    />
                </Box>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>Prefix</strong></TableCell>
                            <TableCell><strong>Provider</strong></TableCell>
                            <TableCell><strong>Password</strong></TableCell>
                            <TableCell><strong>URL</strong></TableCell>
                            <TableCell><strong>Algorithm</strong></TableCell>
                            <TableCell><strong>Workers</strong></TableCell>
                            <TableCell><strong>Req. Hash</strong></TableCell>
                            <TableCell><strong>Unit</strong></TableCell>
                            <TableCell><strong>Config</strong></TableCell>
                            <TableCell><strong>Reseller</strong></TableCell>
                            <TableCell><strong>Fallback Margin</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={orderBy === 'createTime'}
                                    direction={orderBy === 'createTime' ? order : 'desc'}
                                    onClick={() => handleRequestSort('createTime')}
                                >
                                    <strong>Created At</strong>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={orderBy === 'updateTime'}
                                    direction={orderBy === 'updateTime' ? order : 'desc'}
                                    onClick={() => handleRequestSort('updateTime')}
                                >
                                    <strong>Updated At</strong>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell><strong>Actions</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {fetching ? (
                            <TableRow>
                                <TableCell colSpan={15} align="center">
                                    <CircularProgress size={28} />
                                </TableCell>
                            </TableRow>
                        ) : orders.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={15} align="center">
                                    No cloud orders found.
                                </TableCell>
                            </TableRow>
                        ) : (
                            sortedOrders.filter((row) => !showActiveOnly || row.active).map((row) => (
                                <TableRow key={row.id!} hover>
                                    <TableCell>{row.userPrefix}</TableCell>
                                    <TableCell>{row.provider === 'BRAIINS' ? 'Braiins' : 'NiceHash'}</TableCell>
                                    <TableCell>{row.password}</TableCell>
                                    <TableCell>{row.url}</TableCell>
                                    <TableCell>{row.algorithm}</TableCell>
                                    <TableCell>{row.totalWorkers}</TableCell>
                                    <TableCell>{row.requiredHash}</TableCell>
                                    <TableCell>{row.hashRateUnit}</TableCell>
                                    <TableCell>
                                        {configs.find(c => c.id === row.cloudNicehashConfigId)?.name || row.cloudNicehashConfigId || 'None'}
                                    </TableCell>
                                    <TableCell>
                                        {resellers.find(r => r.id === row.resellerId)?.resellerName || row.resellerId || 'None'}
                                    </TableCell>
                                    <TableCell>{row.fallbackMarginPercent ?? 0}%</TableCell>
                                    <TableCell>
                                        <Typography color={row.active ? 'success.main' : 'error.main'} fontWeight="bold">
                                            {row.active ? 'Active' : 'Disabled'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{row.createTime ? new Date(row.createTime).toLocaleString() : '-'}</TableCell>
                                    <TableCell>{row.updateTime ? new Date(row.updateTime).toLocaleString() : '-'}</TableCell>
                                    <TableCell>
                                        <Tooltip title="Edit">
                                            <IconButton size="small" onClick={() => handleOpenEdit(row)}>
                                                <Edit fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={row.active ? "Disable" : "Enable"}>
                                            <IconButton size="small" color={row.active ? "error" : "success"} onClick={() => handleToggleActive(row)}>
                                                {row.active ? <Delete fontSize="small" /> : <Add fontSize="small" />}
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Add / Edit Modal */}
            <Modal open={formOpen} onClose={handleClose}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 500,
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        p: 4,
                    }}
                >
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {formData.id ? 'Edit Cloud Order' : 'Add Cloud Order'}
                    </Typography>

                    <TextField
                        fullWidth
                        select
                        label="Provider"
                        value={formData.provider}
                        onChange={handleChange('provider')}
                        margin="dense"
                        disabled={formData.id !== null}
                        helperText={formData.id !== null ? 'Provider cannot be changed after the order is created.' : undefined}
                    >
                        <MenuItem value="NICEHASH">NiceHash</MenuItem>
                        <MenuItem value="BRAIINS">Braiins</MenuItem>
                    </TextField>

                    <TextField
                        fullWidth select label="Cloud Order Config"
                        value={formData.cloudNicehashConfigId || ''}
                        onChange={handleChange('cloudNicehashConfigId')}
                        margin="dense"
                    >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {configs.map(c => (
                            <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                        ))}
                    </TextField>

                    <TextField fullWidth label="User Prefix" value={formData.userPrefix} onChange={handleChange('userPrefix')} margin="dense" />
                    <TextField fullWidth label="URL" value={formData.url} onChange={handleChange('url')} margin="dense" />
                    <TextField fullWidth label="Start Port" type="number" value={formData.startPort} onChange={handleChange('startPort')} margin="dense" />

                    <Box display="flex" gap={2}>
                        <TextField sx={{ flex: 2 }} fullWidth label="Required Hash" type="number" inputProps={{ step: 'any' }} value={formData.requiredHash} onChange={handleChange('requiredHash')} margin="dense" />
                        <TextField sx={{ flex: 1 }} fullWidth select label="Unit" value={formData.hashRateUnit} onChange={handleChange('hashRateUnit')} margin="dense">
                            <MenuItem value="EH">EH</MenuItem>
                            <MenuItem value="PH">PH</MenuItem>
                            <MenuItem value="TH">TH</MenuItem>
                            <MenuItem value="GH">GH</MenuItem>
                            <MenuItem value="MH">MH</MenuItem>
                        </TextField>
                    </Box>

                    <TextField fullWidth label="Total Workers" type="number" value={formData.totalWorkers} onChange={handleChange('totalWorkers')} margin="dense" />
                    
                    <Autocomplete
                        options={resellers}
                        getOptionLabel={(option) => option.resellerName || ''}
                        value={resellers.find(r => r.id === formData.resellerId) || null}
                        onChange={(_, newValue) => {
                            setFormData({ ...formData, resellerId: newValue ? newValue.id : 0 });
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label="Reseller" margin="dense" fullWidth />
                        )}
                        sx={{ mt: 1, mb: 1 }}
                    />

                    <TextField fullWidth select label="Algorithm" value={formData.algorithm} onChange={handleChange('algorithm')} margin="dense">
                        <MenuItem value="SHA256ASICBOOST">SHA256ASICBOOST</MenuItem>
                        <MenuItem value="KHEAVYHASH">KHEAVYHASH</MenuItem>
                        <MenuItem value="ALEPHIUM">ALEPHIUM</MenuItem>
                        <MenuItem value="SCRYPT">SCRYPT</MenuItem>
                    </TextField>

                    <TextField fullWidth select label="Order Type" value={formData.orderType} onChange={handleChange('orderType')} margin="dense">
                        <MenuItem value="STANDARD">STANDARD</MenuItem>
                        <MenuItem value="FIXED">FIXED</MenuItem>
                        <MenuItem value="BUSINESS">BUSINESS</MenuItem>
                    </TextField>

                    <TextField fullWidth select label="Auto Disconnect" value={formData.autoDisconnect ? 'true' : 'false'} onChange={(e) => setFormData({ ...formData, autoDisconnect: e.target.value === 'true' })} margin="dense">
                        <MenuItem value="true">Yes</MenuItem>
                        <MenuItem value="false">No</MenuItem>
                    </TextField>

                    <TextField fullWidth select label="Should Increment Port" value={formData.shouldIncPort ? 'true' : 'false'} onChange={(e) => setFormData({ ...formData, shouldIncPort: e.target.value === 'true' })} margin="dense">
                        <MenuItem value="true">Yes</MenuItem>
                        <MenuItem value="false">No</MenuItem>
                    </TextField>

                    <TextField fullWidth select label="Status" value={formData.active ? 'true' : 'false'} onChange={(e) => setFormData({ ...formData, active: e.target.value === 'true' })} margin="dense">
                        <MenuItem value="true">Active</MenuItem>
                        <MenuItem value="false">Disabled</MenuItem>
                    </TextField>

                    <TextField fullWidth label="Max Bid Price" type="number" inputProps={{ step: 'any' }} value={formData.maxBidPrice || ''} onChange={handleChange('maxBidPrice')} margin="dense" />

                    <TextField
                        fullWidth
                        label="Fallback Margin (%)"
                        type="number"
                        inputProps={{ min: 0, step: 'any' }}
                        value={formData.fallbackMarginPercent}
                        onChange={handleChange('fallbackMarginPercent')}
                        margin="dense"
                        helperText="Applied only when no machine exists and the reseller is billed from the cloud-order worker mapping."
                    />

                    <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
                        <Button variant="outlined" onClick={handleClose}>Cancel</Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={18} /> : null}
                        >
                            {loading ? 'Saving...' : 'Save'}
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity as any}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default CloudOrders;
